/**
 * Shared Med Error + Fall incident **table** exports (XLSX) for ExportHub and app shell nav.
 * PDF captures the live dashboard via `exportDashboardToPdf` + `IncidentDashboardPdfExportContext`.
 */
import type { ReportingMonth } from '../lib/reportingMonth';
import { exportTableToXlsx } from '../services/exportService';
import { fetchFallIncidentData, type FallIncidentRow } from '../services/fallIncidentData';
import { fetchMedErrorIncidentData, MED_ERROR_DRUG_CATEGORY_LABEL_TH, resolveMedErrorDrugCategory, type MedErrorIncidentRow } from '../services/medErrorIncidentData';
import { formatMedErrorEventDate } from './medErrorDateThai';

/** Excel worksheet tab (≤31 chars). Short ASCII avoids SheetJS errors on long Thai titles. */
export const XLSX_SHEET_MED_ERROR_INCIDENT = 'MedError';
export const XLSX_SHEET_FALL_INCIDENT = 'Fall';

/** Thai fiscal year (ปีงบประมาณ) from Gregorian YYYY-MM. Oct–Sep FY. */
export function reportingMonthToFiscalYear(rm: ReportingMonth): number {
  const [yStr, mStr] = rm.split('-');
  const y = parseInt(yStr ?? '0', 10);
  const m = parseInt(mStr ?? '1', 10);
  const ceYear = Number.isFinite(y) ? y : new Date().getFullYear();
  const month = Number.isFinite(m) ? m : 1;
  return month >= 10 ? ceYear + 544 : ceYear + 543;
}

export function buildMedErrorIncidentExportData(
  rows: MedErrorIncidentRow[],
  deptIdToName: Map<string, string>,
): { headers: string[]; rows: string[][] } {
  const headers = [
    'วันที่เกิดเหตุการณ์',
    'ไตรมาส',
    'ปีงบประมาณ',
    'หน่วยงาน',
    'IPD/OPD',
    'หมวดยา',
    'กลุ่มยา',
    'ระดับความรุนแรง',
    'สาเหตุหลัก',
    'อายุงาน (ปี)',
  ];

  const formatEventDate = (row: MedErrorIncidentRow): string => {
    if (row.eventDateDisplay) return row.eventDateDisplay;
    const d = 'toDate' in row.eventDate ? row.eventDate.toDate() : row.eventDate;
    return formatMedErrorEventDate(d);
  };

  const dataRows = rows.map((row) => [
    formatEventDate(row),
    row.quarter ?? '—',
    String(row.fiscalYear ?? '—'),
    deptIdToName.get(row.departmentId) ?? 'ไม่ทราบชื่อหน่วยงาน',
    row.ipdOpd ?? '—',
    MED_ERROR_DRUG_CATEGORY_LABEL_TH[resolveMedErrorDrugCategory(row.drugCategory)],
    row.drugGroup ?? '—',
    row.severity ?? '—',
    row.rootCause ?? '—',
    row.workExperienceYears != null ? String(row.workExperienceYears) : '—',
  ]);

  return { headers, rows: dataRows };
}

export function buildFallIncidentExportData(
  rows: FallIncidentRow[],
  deptIdToName: Map<string, string>,
): { headers: string[]; rows: string[][] } {
  const headers = [
    'วันที่เกิดเหตุการณ์',
    'หน่วยงาน',
    'Ward',
    'IPD/OPD',
    'ไตรมาส',
    'ปีงบประมาณ',
    'ระดับความรุนแรง',
    'สาเหตุหลัก',
    'สาเหตุย่อย',
  ];

  const formatEventDate = (row: FallIncidentRow): string => {
    const d = 'toDate' in row.eventAt ? row.eventAt.toDate() : row.eventAt;
    return formatMedErrorEventDate(d);
  };

  const dataRows = rows.map((row) => [
    formatEventDate(row),
    deptIdToName.get(row.departmentId) ?? 'ไม่ทราบชื่อหน่วยงาน',
    row.wardRaw ?? '—',
    row.ipdOpd ?? '—',
    row.fiscalQuarter ?? '—',
    String(row.fiscalYear ?? '—'),
    row.severityLevel ?? '—',
    row.causeMain ?? '—',
    row.causeSub ?? '—',
  ]);

  return { headers, rows: dataRows };
}

type DeptLike = { id: string; name?: string };

function deptMap(departments: DeptLike[]): Map<string, string> {
  return new Map(departments.map((d) => [d.id, d.name ?? 'ไม่ทราบชื่อหน่วยงาน']));
}

function departmentFilterLabel(departmentIds: string[] | undefined, deptIdToName: Map<string, string>): string | null {
  if (!departmentIds?.length) return null;
  return departmentIds.map((id) => deptIdToName.get(id) ?? 'ไม่ทราบชื่อหน่วยงาน').join(', ');
}

export type MedErrorIncidentExportFilters = {
  /** `[]` or `undefined` => all allowed scope */
  departmentIds?: string[];
  /** Fiscal year in Buddhist era (e.g. 2568) */
  fiscalYear?: number;
  /** Quarter label matching the imported data (typically `1/2568` .. `4/2568`) */
  quarter?: string;
  severity?: string;
  /** Root cause (สาเหตุหลัก) */
  rootCause?: string;
  ipdOpd?: string;
};

export type FallIncidentExportFilters = {
  /** `[]` or `undefined` => all rows */
  departmentIds?: string[];
  /** Fiscal year in Buddhist era (e.g. 2568) */
  fiscalYear?: number;
  /** Quarter label matching the imported data (typically `1/2568` .. `4/2568`) */
  fiscalQuarter?: string;
  severityLevel?: string;
  /** Main cause (สาเหตุหลัก) */
  causeMain?: string;
  ipdOpd?: string;
  /**
   * Calendar month key `YYYY-MM` (e.g. `2026-04`).
   * Uses `eventYearMonth` when available; otherwise derives from `eventAt`.
   */
  eventYearMonth?: string;
};

function toYearMonthKeyFromEventAt(row: { eventAt: { toDate: () => Date } | Date }): string | null {
  const d = 'toDate' in row.eventAt ? row.eventAt.toDate() : row.eventAt;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/** Same scope as ExportHub med_error_incident branch, with optional filters applied. */
export async function downloadMedErrorIncidentExport(
  departments: DeptLike[],
  reportingMonth: ReportingMonth,
  filters?: MedErrorIncidentExportFilters,
): Promise<void> {
  const defaultFiscalYear = reportingMonthToFiscalYear(reportingMonth);
  const fiscalYear = filters?.fiscalYear ?? defaultFiscalYear;
  const rows = await fetchMedErrorIncidentData({
    departmentIds: (filters?.departmentIds?.length ? filters.departmentIds : ['*']) as string[],
    fiscalYear,
    quarter: filters?.quarter,
    severity: filters?.severity,
    rootCause: filters?.rootCause,
    ipdOpd: filters?.ipdOpd,
  });
  const deptIdToName = deptMap(departments);
  const { headers: exportHeaders, rows: dataRows } = buildMedErrorIncidentExportData(rows, deptIdToName);

  const metaRows: string[][] = [['ปีงบประมาณ', String(fiscalYear)]];
  const departmentLabel = departmentFilterLabel(filters?.departmentIds, deptIdToName);
  if (departmentLabel) metaRows.unshift(['หน่วยงาน', departmentLabel]);
  else metaRows.unshift(['หน่วยงาน', 'ทุกหน่วยงาน']);
  if (filters?.quarter) metaRows.push(['ไตรมาส', filters.quarter]);
  if (filters?.severity) metaRows.push(['ระดับความรุนแรง', filters.severity]);
  if (filters?.rootCause) metaRows.push(['สาเหตุหลัก', filters.rootCause]);
  if (filters?.ipdOpd) metaRows.push(['IPD/OPD', filters.ipdOpd]);

  const sheetRows: string[][] = [...metaRows, [], exportHeaders, ...dataRows];
  await exportTableToXlsx(
    sheetRows,
    XLSX_SHEET_MED_ERROR_INCIDENT,
    `med-error-incident-${fiscalYear}.xlsx`,
  );
}

/** Same cap as FallIncidentDashboard / ExportHub fall branch, with optional filters applied. */
export async function downloadFallIncidentExport(
  departments: DeptLike[],
  reportingMonth: ReportingMonth,
  filters?: FallIncidentExportFilters,
): Promise<void> {
  const defaultFiscalYear = reportingMonthToFiscalYear(reportingMonth);
  const fiscalYear = filters?.fiscalYear ?? defaultFiscalYear;
  const rows = await fetchFallIncidentData({
    limit: 1000,
    fiscalYear,
    fiscalQuarter: filters?.fiscalQuarter,
    severityLevel: filters?.severityLevel,
    causeMain: filters?.causeMain,
    departmentIds: filters?.departmentIds,
  });

  const filtered = rows.filter((r) => {
    if (filters?.ipdOpd) {
      const s = (r.ipdOpd ?? '').trim().toUpperCase();
      if (s !== filters.ipdOpd.trim().toUpperCase()) return false;
    }
    if (filters?.eventYearMonth) {
      const key = (r.eventYearMonth ?? '').trim() || toYearMonthKeyFromEventAt(r) || '';
      if (key !== filters.eventYearMonth) return false;
    }
    return true;
  });

  const deptIdToName = deptMap(departments);
  const { headers: exportHeaders, rows: dataRows } = buildFallIncidentExportData(filtered, deptIdToName);
  const metaRows: string[][] = [['ปีงบประมาณ', String(fiscalYear)]];
  const departmentLabel = departmentFilterLabel(filters?.departmentIds, deptIdToName);
  if (departmentLabel) metaRows.unshift(['หน่วยงาน', departmentLabel]);
  else metaRows.unshift(['ขอบเขต', 'ทุกหน่วยงาน (ข้อมูลนำเข้า)']);
  if (filters?.fiscalQuarter) metaRows.push(['ไตรมาส', filters.fiscalQuarter]);
  if (filters?.eventYearMonth) metaRows.push(['เดือน', filters.eventYearMonth]);
  if (filters?.severityLevel) metaRows.push(['ระดับความรุนแรง', filters.severityLevel]);
  if (filters?.causeMain) metaRows.push(['สาเหตุหลัก', filters.causeMain]);
  if (filters?.ipdOpd) metaRows.push(['IPD/OPD', filters.ipdOpd]);
  const sheetRows: string[][] = [...metaRows, [], exportHeaders, ...dataRows];
  await exportTableToXlsx(sheetRows, XLSX_SHEET_FALL_INCIDENT, `fall-incident-${fiscalYear}.xlsx`);
}
